// "로그인된 기기" - 내 계정으로 로그인된 기기(세션) 목록. 특정 기기만 로그아웃하거나
// 나머지 전체를 한 번에 로그아웃할 수 있다. PushSettings.js와 같은 자리(프로필 화면,
// 사용자별 개인 설정) - 2026-10-16 416일 토큰 → 짧은 액세스 토큰 + 리프레시 세션 전환.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as authApi from '../../lib/api/auth';

function formatDt(dt) {
  if (!dt) return '-';
  try {
    return new Date(dt).toLocaleString('ko-KR', { hour12: false });
  } catch {
    return dt;
  }
}

function deviceTypeLabel(deviceType) {
  return deviceType === 'android' ? 'KMES 앱' : '웹';
}

const LoginSessions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // 개별 로그아웃 처리중 id
  const [revokingOthers, setRevokingOthers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authApi.getSessions();
      setSessions(res.data.list || []);
    } catch (e) {
      enqueueSnackbar('로그인된 기기 목록을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (sessionId) => {
    setBusyId(sessionId);
    try {
      await authApi.revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      enqueueSnackbar('해당 기기를 로그아웃했습니다.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('로그아웃에 실패했습니다.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeOthers = async () => {
    setRevokingOthers(true);
    try {
      await authApi.revokeOtherSessions();
      enqueueSnackbar('다른 기기를 모두 로그아웃했습니다.', { variant: 'success' });
      load();
    } catch (e) {
      enqueueSnackbar('처리에 실패했습니다.', { variant: 'error' });
    } finally {
      setRevokingOthers(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const otherCount = (sessions || []).filter((s) => !s.current).length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          이 계정으로 로그인된 기기 {sessions ? sessions.length : 0}개
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={otherCount === 0 || revokingOthers}
          onClick={handleRevokeOthers}
        >
          {revokingOthers ? '처리중...' : '다른 기기 모두 로그아웃'}
        </Button>
      </Stack>

      <List dense disablePadding>
        {(sessions || []).map((s, idx) => (
          <React.Fragment key={s.sessionId}>
            {idx > 0 && <Divider component="li" />}
            <ListItem
              secondaryAction={
                !s.current && (
                  <Button
                    size="small"
                    color="error"
                    disabled={busyId === s.sessionId}
                    onClick={() => handleRevoke(s.sessionId)}
                  >
                    {busyId === s.sessionId ? '처리중...' : '로그아웃'}
                  </Button>
                )
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{s.deviceName || '알 수 없는 기기'}</Typography>
                    <Chip size="small" label={deviceTypeLabel(s.deviceType)} variant="outlined" />
                    {s.current && <Chip size="small" label="현재 기기" color="primary" />}
                  </Stack>
                }
                secondary={`마지막 사용: ${formatDt(s.lastUsedDt)} · IP ${s.ipLast || '-'}`}
              />
            </ListItem>
          </React.Fragment>
        ))}
        {(!sessions || sessions.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            로그인된 기기가 없습니다.
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default LoginSessions;
